import { CarryFailure, CarryMember, CarryRequest, CarryResult } from '../types/coachCarry';
import { carryKoreanMessage, CarryMemberDetails } from './coachCarryDisplay';

export const CARRY_MEMBER_LIMIT = 30;
export const CARRY_LIMIT_MESSAGE = '한 번에 최대 30명까지 이어받을 수 있어요. 나머지 회원은 완료 후 다시 진행해 주세요.';

export type CarrySelection = { coachId: string; toCenterId: string; idempotencyKey: string };

const newCarryKey = () => {
    const bytes = new Uint8Array(16);
    window.crypto.getRandomValues(bytes);
    return `carry-${Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('')}`;
};

export const carrySelectionFor = (
    previous: CarrySelection | null, coachId: string, toCenterId: string,
    createKey: () => string = newCarryKey,
): CarrySelection => previous?.coachId === coachId && previous.toCenterId === toCenterId
    ? previous : { coachId, toCenterId, idempotencyKey: createKey() };

export const selectCarryMembers = (members: CarryMember[], requestedIds: string[]) => {
    const allowed = new Set(members.filter((member) => member.canCarry).map((member) => member.memberId));
    const eligible = Array.from(new Set(requestedIds)).filter((id) => allowed.has(id));
    return { memberIds: eligible.slice(0, CARRY_MEMBER_LIMIT), exceeded: eligible.length > CARRY_MEMBER_LIMIT };
};

export const createCarryRequest = (selection: CarrySelection, memberIds: string[], consent: boolean): CarryRequest => {
    if (!consent || memberIds.length < 1 || memberIds.length > CARRY_MEMBER_LIMIT) {
        throw new Error('이어받을 회원과 동의를 확인해 주세요.');
    }
    return {
        idempotencyKey: selection.idempotencyKey,
        toCenterId: selection.toCenterId,
        memberIds: [...memberIds],
        consent: { checked: true, noticeVersion: '2026-10' },
    };
};

export const carryResultView = (result: CarryResult) => {
    if (result.outcome === 'COMMITTED' || result.outcome === 'REPLAYED') {
        return { label: '완료', retry: false, pending: false, restart: false };
    }
    if (result.outcome === 'PENDING_CONFIRMATION') {
        return { label: '확인 중', retry: true, pending: true, restart: false };
    }
    return {
        label: '실패', pending: false,
        retry: result.outcome === 'FAILED' && result.details?.retryable === true,
        restart: result.errorCode === 'CARRY_IDEMPOTENCY_KEY_REUSED',
    };
};

export const canDiscardCarryFailure = (status: number, hasPreviousAttempt: boolean, errorCode?: string) =>
    !hasPreviousAttempt && ([400, 401, 403, 404].includes(status)
        || (status === 503 && errorCode === 'CARRY_PREREQUISITE_INDEXES_MISSING')
        || (status === 500 && errorCode === 'CARRY_TEMPLATE_CONFLICT'));

const ERROR_MESSAGES: Record<string, string> = {
    CARRY_CONSENT_REQUIRED: '회원에게 이동 사실을 알리고 동의를 받은 뒤 체크해 주세요.',
    CARRY_MEMBER_COUNT_INVALID: '이어받을 회원을 1명 이상, 30명 이하로 선택해 주세요.',
    CARRY_SAME_CENTER: '받는 센터는 현재 센터와 다르게 선택해 주세요.',
    ACCESS_TOKEN_REQUIRED: '로그인이 만료됐어요. 다시 로그인해 주세요.',
    CARRY_FORBIDDEN: '이 센터의 관리자만 이어받기를 진행할 수 있어요.',
    CARRY_COACH_NOT_IN_FROM_CENTER: '현재 센터에 소속된 강사를 찾지 못했어요. 강사 정보를 다시 확인해 주세요.',
    CARRY_JOB_NOT_FOUND: '작업을 찾지 못했어요. 같은 요청으로 다시 확인해 주세요.',
    CARRY_PREREQUISITE_INDEXES_MISSING: '지금은 이어받기를 진행할 수 없어요. 잠시 후 다시 시도하고, 계속되면 라포에 문의해 주세요.',
    CARRY_TEMPLATE_CONFLICT: '받는 센터의 수강권을 준비하지 못했어요. 라포에 문의해 주세요.',
    CARRY_IDEMPOTENCY_KEY_REUSED: '이전 요청과 받는 센터 또는 강사가 달라요. 처음부터 다시 진행해 주세요.',
    CARRY_TARGET_COACH_NOT_ACTIVE: '강사가 받는 센터에 먼저 소속돼야 해요.',
    CARRY_TRANSACTION_UNAVAILABLE: '지금은 이어받기를 진행할 수 없어요. 라포에 문의해 주세요.',
    CARRY_STATE_INCONSISTENT: '이동 결과를 확인할 수 없어요. 라포에 문의해 주세요.',
    ALREADY_CARRIED: '이미 다른 요청으로 이어받은 회원이에요. 회원 정보를 다시 확인해 주세요.',
    CARRY_TARGET_SET_CHANGED: '수강권 목록이 바뀌었어요. 미리보기를 다시 확인한 뒤 재시도해 주세요.',
    CARRY_SOURCE_CHANGED: '수강권 정보가 바뀌었어요. 미리보기를 다시 확인한 뒤 재시도해 주세요.',
};

export const carryErrorMessage = (status: number, data?: CarryFailure, targets = false): string => {
    if (targets && status === 404) return '받는 센터 목록을 불러오지 못했어요';
    // 한국어로 제공되는 서버 메시지만 사용한다. Joi 문구나 영어 코드값은 노출하지 않는다.
    const message = data?.message ? carryKoreanMessage(data.message) : null;
    if (message) return message;
    if (data?.errorCode && ERROR_MESSAGES[data.errorCode]) return ERROR_MESSAGES[data.errorCode];
    const fallback: Record<number, string> = {
        0: '응답을 확인하지 못했어요. 같은 요청으로 다시 확인해 주세요.',
        400: '입력한 내용을 확인하고 다시 시도해 주세요.',
        401: '로그인이 만료됐어요. 다시 로그인해 주세요.',
        403: '이 작업을 진행할 권한이 없어요. 센터 관리자에게 확인해 주세요.',
        404: '요청한 정보를 찾지 못했어요. 새로고침 후 다시 확인해 주세요.',
        409: '회원이나 수강권 상태가 바뀌었어요. 내용을 다시 확인해 주세요.',
        503: '잠시 후 다시 시도해 주세요. 계속되면 라포에 문의해 주세요.',
        500: '처리를 마치지 못했어요. 잠시 후 다시 시도해 주세요.',
    };
    return fallback[status] || fallback[500];
};

export type SavedCarry = {
    body: CarryRequest; memberNames: Record<string, string>; targetName: string;
    memberDetails?: Record<string, CarryMemberDetails>;
    sharedWarnings?: string[];
    rateTableMissing?: boolean;
};
export const carryStorageKey = (actorId: string, centerId: string, coachId: string) =>
    `coach-carry:${actorId}:${centerId}:${coachId}`;

export const readSavedCarry = (key: string): SavedCarry | null => {
    try {
        const saved = JSON.parse(sessionStorage.getItem(key) || 'null');
        if (saved?.body?.consent?.checked !== true || saved.body.consent.noticeVersion !== '2026-10'
            || typeof saved.body.idempotencyKey !== 'string' || typeof saved.body.toCenterId !== 'string'
            || !Array.isArray(saved.body.memberIds) || saved.body.memberIds.length < 1
            || saved.body.memberIds.length > CARRY_MEMBER_LIMIT
            || !saved.body.memberIds.every((id: unknown) => typeof id === 'string')
            || !saved.memberNames || typeof saved.memberNames !== 'object'
            || !Object.values(saved.memberNames).every((name) => typeof name === 'string')
            || typeof saved.targetName !== 'string') return null;
        // 예전 저장 형식도 복구하고, 표시용 정보가 손상돼도 원래 요청은 유지한다.
        if (saved.memberDetails !== undefined && (!saved.memberDetails || typeof saved.memberDetails !== 'object' || !Object.values(saved.memberDetails).every((value: any) =>
            value && Array.isArray(value.memberships) && value.memberships.every((item: any) =>
                item && typeof item.text === 'string' && typeof item.unpaid === 'string'
                && (item.title === undefined || typeof item.title === 'string'))
            && Array.isArray(value.warnings) && value.warnings.every((warning: unknown) => typeof warning === 'string')))) {
            delete saved.memberDetails;
        }
        if (saved.sharedWarnings !== undefined && (!Array.isArray(saved.sharedWarnings)
            || !saved.sharedWarnings.every((warning: unknown) => typeof warning === 'string'))) delete saved.sharedWarnings;
        if (saved.rateTableMissing !== undefined && typeof saved.rateTableMissing !== 'boolean') delete saved.rateTableMissing;
        Object.values(saved.memberDetails || {}).forEach((detail: any) => {
            detail.warnings = detail.warnings.filter((warning: string) => !warning.startsWith('미수는 '));
            detail.memberships.forEach((membership: any) => {
                if (typeof membership.unpaidAmount !== 'number' || !Number.isSafeInteger(membership.unpaidAmount)
                    || membership.unpaidAmount < 0) delete membership.unpaidAmount;
            });
        });
        return saved;
    } catch { return null; }
};
